/* Thin client for the Experiment Studio backend (Express + @uals/skill-runtime). */
import { reportError } from "./bugReporter.js";

const SLUG = "stroop-adaptive";

/* True when the app is running inside a (cross-origin) third-party iframe. Used to adapt the
 * OAuth login flow: a full-frame navigation to the provider is frame-busted (providers send
 * X-Frame-Options: DENY), so when embedded we open login in a top-level popup instead.
 * Reading window.top across origins throws — treat that as "embedded". */
export const isEmbedded = (() => {
  if (typeof window === "undefined") return false;
  try { return window.self !== window.top; } catch { return true; }
})();

/* Dev role switch: persisted locally and sent as x-dev-role so the four dashboards are
 * demoable from one browser. The server honours it ONLY when no real OAuth session is
 * present (see server/src/auth.js) — in production the logged-in session decides the role. */
const ROLE_KEY = "cohort.devRole";
export const devRole = {
  get: () => { try { return localStorage.getItem(ROLE_KEY) || ""; } catch { return ""; } },
  set: (r) => { try { r ? localStorage.setItem(ROLE_KEY, r) : localStorage.removeItem(ROLE_KEY); } catch { /* ignore */ } },
};

/* "Act as" — a real, signed-in user temporarily working as a LOWER role they're entitled to
 * (e.g. a researcher acting as participant). Sent as x-assume-role; the server caps it to the
 * account's granted ceiling, so it can never escalate. Distinct from the dev role switch. */
const ASSUME_KEY = "onset.assumeRole";
export const assumeRole = {
  get: () => { try { return localStorage.getItem(ASSUME_KEY) || ""; } catch { return ""; } },
  set: (r) => { try { r ? localStorage.setItem(ASSUME_KEY, r) : localStorage.removeItem(ASSUME_KEY); } catch { /* ignore */ } },
};

async function j(method, url, body) {
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  const r = devRole.get();
  if (r) headers["x-dev-role"] = r;
  const a = assumeRole.get();
  if (a) headers["x-assume-role"] = a;
  // Carry the current UI language so server-side LLM features (paper analysis, etc.)
  // answer in the user's preferred language. <html lang> is kept current by i18n.jsx.
  const lang = (typeof document !== "undefined" && document.documentElement.lang) || "";
  if (lang) headers["x-app-lang"] = lang;
  const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json()).error || ""; } catch { /* ignore */ }
    // Auto-report server-side failures, but skip auth noise (401/403) — those are expected
    // when a session expires or a role lacks access, not bugs.
    if (res.status !== 401 && res.status !== 403) {
      reportError({ message: `${method} ${url} → ${res.status}${detail ? " · " + detail : ""}`,
        severity: res.status >= 500 ? "error" : "warning", route: url,
        context: { statusCode: res.status, method } });
    }
    throw new Error(`${method} ${url} → ${res.status}${detail ? " · " + detail : ""}`);
  }
  return res.json();
}

export const api = {
  // identity + role layer
  me: () => j("GET", "/api/me"),
  health: () => j("GET", "/api/health"),
  dashboard: () => j("GET", "/api/dashboard"),
  users: () => j("GET", "/api/users"),
  setUserRole: (id, appRole) => j("PUT", `/api/users/${id}/role`, { appRole }),

  // experiments + ownership/assignment
  experiments: () => j("GET", "/api/experiments"),
  createExperiment: (title, slug) => j("POST", "/api/experiments", { title, slug }),
  setTeam: (slug, raIds) => j("PUT", `/api/experiments/${slug}/team`, { raIds }),
  deleteExperiment: (slug) => j("DELETE", `/api/experiments/${slug}`),
  // Community gallery: list published studies; publish/unpublish one (owner/admin).
  gallery: () => j("GET", "/api/experiments/gallery"),
  publishExperiment: (slug, published) => j("PUT", `/api/experiments/${slug}/publish`, { published }),
  participants: (slug) => j("GET", `/api/experiments/${slug}/participants`),
  enrol: (slug, participantId) => j("POST", `/api/experiments/${slug}/participants`, { participantId }),
  consent: (slug, consent = true) => j("POST", `/api/experiments/${slug}/consent`, { consent }),
  // QR self-enrolment: landing info + a signed-in participant joining themselves.
  joinInfo: (slug) => j("GET", `/api/experiments/${slug}/join`),
  join: (slug) => j("POST", `/api/experiments/${slug}/join`),

  // paradigm template library + paper analysis
  paradigms: () => j("GET", "/api/paradigms"),
  paradigm: (id) => j("GET", `/api/paradigms/${id}`),
  analyzePaper: (text) => j("POST", "/api/paradigms/analyze", { text }),
  // AI tutor: a grounded conversation about one paradigm (educational). messages: [{role,content}]
  paradigmChat: (id, messages) => j("POST", `/api/paradigms/${id}/chat`, { messages }),

  // Explore (literature-to-design): discover real studies, summarize, ideate, design a new spec.
  paradigmStudies: (id, { q = "", recent = false } = {}) =>
    j("GET", `/api/paradigms/${id}/studies?${new URLSearchParams({ ...(q ? { q } : {}), ...(recent ? { recent: "1" } : {}) }).toString()}`),
  summarizeStudy: (id, study) => j("POST", `/api/paradigms/${id}/studies/summarize`, { study }),
  exploreIdeate: (id, messages, studies) => j("POST", `/api/paradigms/${id}/explore/ideate`, { messages, studies }),
  // Ask the LLM for up to 6 alternative research questions / hypotheses, grounded in the dialogue so far.
  exploreSuggest: (id, payload) => j("POST", `/api/paradigms/${id}/explore/suggest`, payload), // {field:'question'|'hypothesis', question, hypothesis, messages, studies}
  exploreDesign: (id, payload) => j("POST", `/api/paradigms/${id}/explore/design`, payload), // {question,hypothesis,notes}
  // Create a new experiment from a spec (template or paper-derived) and return its meta.
  // `meta` carries lineage: { paradigm, derivedFrom, question, hypothesis } — derivedFrom links
  // this study to the template/experiment it descends from, documenting the series.
  createFromSpec: async (title, spec, meta = {}) => {
    const m = await j("POST", "/api/experiments", { title, ...meta });
    await j("PUT", `/api/experiments/${m.slug}/spec`, spec);
    return m;
  },
  // Clone an existing experiment: copy its spec into a NEW owned study (researcher-customizable),
  // linked back to its source so the lineage tree records the clone.
  cloneExperiment: async (slug, title) => {
    const spec = await j("GET", `/api/experiments/${slug}/spec`);
    const { _savedAt, _savedBy, ...clean } = spec || {};   // drop server-write metadata
    const base = `${title || slug} (copy)`;
    const m = await j("POST", "/api/experiments", { title: base, derivedFrom: slug });
    await j("PUT", `/api/experiments/${m.slug}/spec`, clean);
    return m;
  },
  // Lineage: the ancestor chain (root → this) + the studies derived from a given experiment.
  getLineage: (slug) => j("GET", `/api/experiments/${slug}/lineage`),

  // experiment data (existing)
  getSpec: (slug = SLUG) => j("GET", `/api/experiments/${slug}/spec`),
  saveSpec: (spec, slug = SLUG) => j("PUT", `/api/experiments/${slug}/spec`, spec),
  saveRun: (run, slug = SLUG) => j("POST", `/api/experiments/${slug}/runs`, run),
  listRuns: (slug = SLUG) => j("GET", `/api/experiments/${slug}/runs`),
  data: (slug = SLUG) => j("GET", `/api/experiments/${slug}/data`),

  // XGH login proxy (Google) + SMTP proxy. The gateway token stays server-side.
  session: () => j("GET", "/auth/me"),                          // { user, authenticated }
  login: (returnTo = window.location.pathname + window.location.search) => {
    const url = "/auth/login?return=" + encodeURIComponent(returnTo);
    // Embedded: the OAuth provider can't render in our iframe, so drive login in a
    // top-level popup. The returned page (our origin) posts "onset:authed" back to the
    // opener and closes itself — Shell.jsx listens and refreshes. Fall back to a full-frame
    // navigation if the popup is blocked.
    if (isEmbedded) {
      const w = window.open(url, "onset-login", "width=480,height=680");
      if (!w) window.location.href = url;
      return w || null;
    }
    window.location.href = url;
    return null;
  },
  // Email + password (SMTP-verified) registration & login — see server/src/gateway.js.
  register: (email, password, name) => j("POST", "/auth/register", { email, password, name }),
  verifyEmail: (email, code) => j("POST", "/auth/verify", { email, code }),       // → sets session
  resendCode: (email) => j("POST", "/auth/resend-code", { email }),
  passwordLogin: (email, password) => j("POST", "/auth/password-login", { email, password }), // → sets session
  forgotPassword: (email) => j("POST", "/auth/forgot-password", { email }),
  resetPassword: (email, code, newPassword) => j("POST", "/auth/reset-password", { email, code, newPassword }),
  logout: () => j("POST", "/auth/logout"),
  sendEmail: (msg) => j("POST", "/api/email/send", msg),         // {to,subject,body|html,cc?,bcc?}
};
